import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import {
  ELSCheckBox,
  ELSTextBox
} from '@els/els-component-form-field-react';
import FocusTrap from 'focus-trap-react';
import {
  useHistory,
} from 'react-router';
import { ELSPageLoader } from '@els/els-ui-common-react';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import { flowRight } from 'lodash';
import 'chartjs-plugin-datalabels';
import {
  AdminChatIsbnBookDto,
  LlmModels,
  VectorSearchDto,
  VectorSearchResponseDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { ELSDataTable } from '../../components/els.components';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import AuthoringIsbnModal, { AUTHORING_ISBN_MODAL_ID } from './AuthoringIsbnModal.component';
import {
  isAdminUser
} from '../ai-chat/ai-chat.utilities';
import { RoutePath } from '../../components/app/app.constants';
import { copyContentToClipboard } from '../../utilities/common.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  adminPromptAction,
  adminSearchVectorAction,
  fetchAdminChatEeoIsbnsAction,
  fetchChatEeoIsbnsAction
} from '../../components/app/app-state-actions.utilities';
import { RecordEmpty } from '../../models/common.models';

export type AuthoringProps = ELSPropsFromToastService & ELSPropsFromModalService;

export type AuthoringState = {
  search: string;
  searchResults: VectorSearchResponseDto['results'];
  prompt: string;
  response: string;
  selectedSearchResults: Record<string, boolean>;
  selectedEeoIsbns: Record<string, boolean>;
  books: AdminChatIsbnBookDto[];
  isAdminUser: boolean;
}

const defaultState: AuthoringState = {
  searchResults: [],
  search: '',
  prompt: '',
  response: '',
  selectedSearchResults: {},
  selectedEeoIsbns: {},
  books: [],
  isAdminUser: false
};

type TableRow = {
  isbn: string;
  book: string;
  chapter: string;
  section: string;
  id: string;
  content: string;
  score: number | string;
  page: number | string;
  chapterNumber: number | string;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AuthoringComponent = (props: AuthoringProps): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const history = useHistory();

  const [state, setState] = useState<AuthoringState>({
    ...defaultState,
    isAdminUser: isAdminUser()
  });

  const mergeState = (newState: Partial<AuthoringState>) => {
    setState((prevState) => {
      return {
        ...prevState,
        ...newState
      };
    });
  };

  useEffect(() => {

    if (!state.isAdminUser) {
      history.push(RoutePath.PAGE_NOT_FOUND);
      return;
    }

    fetchChatEeoIsbnsAction({
      appState,
      appStateRef,
      setAppState
    }).then((eeoIsbns) => {

      fetchAdminChatEeoIsbnsAction({
        appState,
        appStateRef,
        setAppState,
        isbns: eeoIsbns
      }).then((response) => {
        if (!response || !response.length) {
          return;
        }

        const supportedBooks = response.filter((book) => {
          return book.isActive;
        });

        mergeState({
          books: supportedBooks,
          selectedEeoIsbns: supportedBooks.reduce((acc, book) => {
            return {
              ...acc,
              [book.isbn]: true
            };
          }, {})
        });
      });
    });

  }, []);

  const viewBookChunk = (row) => {
    const { modalService } = props;
    const modalId = 'viewBookChunk';
    modalService.openAlertModal({
      modalId,
      content: (
        <div className="">
          {row.content}
        </div>
      ),
      confirmHandler: () => {
        modalService.closeModal(modalId);
      },
    });
  };

  const getNumber = (text: string): number | string => {
    if (isNaN(Number(text))) {
      return text;
    }
    return Number(text);
  };

  const mapTableData = (result: VectorSearchResponseDto['results'][0]): TableRow => {
    const contentProps = result._source.content_props as { chapter_number?: string | number; page?: { start_page?: { number?: string | number } } };
    const breadCrumbs = result._source.bread_crumbs || [];
    return {
      isbn: result._source.source_id,
      book: result._source.title,
      chapterNumber: getNumber(String(contentProps.chapter_number ?? '')),
      chapter: breadCrumbs[0]?.title,
      section: breadCrumbs[1] ? breadCrumbs[1].title : null,
      id: result._source.chunk_id,
      content: result._source.chunk_text,
      page: getNumber(String(contentProps.page?.start_page?.number ?? '')),
      score: getNumber(result._score)

    };
  };

  const getBookChunks = () => {
    if (!state.searchResults || !state.searchResults.length) {
      return '';
    }

    const chunks = state.searchResults
      .filter((result) => state.selectedSearchResults[result._source.chunk_id])
      .map(mapTableData)
      .map((chunk) => {
        return `<book_chunk>
<book_title>${chunk.book}</book_title>
<chapter_title>${chunk.chapter}</chapter_title>
<chunk>${chunk.content}</chunk>
</book_chunk>`;
      }).join('\n');

    return `<book_chunks>
${chunks}
</book_chunks>`;
  };

  const buildFinalPrompt = () => {

    const bookChunks = getBookChunks();

    return `You are an authoring bot.  Use the book_chunks to augment your knowledge.

${bookChunks}

${state.prompt}`;
  };

  const viewPromptPreview = () => {
    const { modalService } = props;
    const modalId = 'viewPromptPreview';
    modalService.openAlertModal({
      modalId,
      content: (
        <div className="">
          <pre className="c-els-authoring__pre">{buildFinalPrompt()}</pre>
        </div>
      ),
      confirmHandler: () => {
        modalService.closeModal(modalId);
      },
    });
  };

  const getSelectAllSearchResults = (searchResults: VectorSearchResponseDto['results']): Record<string, boolean> => {
    if (!searchResults) {
      return {};
    }
    return searchResults.reduce((acc, result) => {
      return {
        ...acc,
        [result._source.chunk_id]: true
      };
    }, {});
  };
  const handleSearch = () => {

    if (!state.search) {
      props.toastService.openToast({
        ...BASE_TOAST_CONFIG,
        component: (
          <div>
            <p>Search is empty</p>
          </div>
        ),
        type: ELSWithToastService.types.NEGATIVE
      });
      return;
    }

    const selectedIsbns = Object.keys(state.selectedEeoIsbns).filter((eeoIsbn) => state.selectedEeoIsbns[eeoIsbn]);

    if (!selectedIsbns.length) {
      props.toastService.openToast({
        ...BASE_TOAST_CONFIG,
        component: (
          <div>
            <p>No isbns selected</p>
          </div>
        ),
        type: ELSWithToastService.types.NEGATIVE
      });
      return;
    }

    const vectorDto: VectorSearchDto = {
      query_text: state.search,
      dsl_filter: {
        bool: {
          must: [
            {
              term: {
                content_type: 'EBK'
              }
            }
          ],
          filter: {
            bool: {
              should: Object.keys(state.selectedEeoIsbns).map((eeoIsbn) => {
                return {
                  term: {
                    identifier: eeoIsbn
                  }
                };
              })
            }
          }
        }
      },
      number_of_results_to_provide: 20,
      number_of_results_to_try: 20,
      minimum_chunk_length: 0,
      k: 50
    };

    adminSearchVectorAction({
      appState,
      appStateRef,
      setAppState,
      vectorDto
    }).then((response) => {
      mergeState({
        searchResults: response.results,
        selectedSearchResults: getSelectAllSearchResults(response.results)
      });
    });
  };

  const handlePrompt = () => {

    if (!state.prompt) {
      props.toastService.openToast({
        ...BASE_TOAST_CONFIG,
        component: (
          <div>
            <p>Prompt is empty</p>
          </div>
        ),
        type: ELSWithToastService.types.NEGATIVE
      });
      return;
    }

    const selectedChunks = Object.keys(state.selectedSearchResults).filter((key) => state.selectedSearchResults[key]);

    if (
      !state.searchResults
      || !state.searchResults.length
      || !selectedChunks.length
    ) {
      props.toastService.openToast({
        ...BASE_TOAST_CONFIG,
        component: (
          <div>
            <p>Book chunks is empty</p>
          </div>
        ),
        type: ELSWithToastService.types.NEGATIVE
      });
      return;
    }

    adminPromptAction({
      appState,
      appStateRef,
      setAppState,
      llmPromptDto: {
        model: LlmModels.claude_4_sonnet,
        temperature: 0,
        messages: [
          {
            role: 'user',
            content: buildFinalPrompt()
          }
        ],
        stream: false
      }
    }).then((response) => {
      mergeState({ response: response.choices[0]?.message?.content || '' });
    });
  };

  const getTableData = (): TableRow[] => {
    return state.searchResults.map(mapTableData);
  };

  const isSearchChecked = (row: TableRow, selectedMap: Record<string, boolean>) => {
    return selectedMap[row.id];
  };

  const handleSearchCheck = (row: TableRow, isSelected: boolean) => {

    setState((prevState) => {
      return {
        ...prevState,
        selectedSearchResults: {
          ...prevState.selectedSearchResults,
          [row.id]: isSelected
        }
      };
    });
  };

  const selectAll = () => {
    setState((prevState) => {
      return {
        ...prevState,
        selectedSearchResults: getSelectAllSearchResults(state.searchResults)
      };
    });
  };

  const deselectAll = () => {
    setState((prevState) => {
      return {
        ...prevState,
        selectedSearchResults: {}
      };
    });
  };

  const handleCopy = () => {
    copyContentToClipboard(state.response);
    props.toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: (
        <div>Successfully copied</div>
      ),
      type: ELSWithToastService.types.POSITIVE
    });
  };

  const handleSelectedIsbnModalOpen = () => {
    const {
      modalService,
    } = props;
    modalService.openModal({
      modalId: AUTHORING_ISBN_MODAL_ID,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      noFocusTrap: true,
      content: (
        <FocusTrap focusTrapOptions={{
          preventScroll: true
        }}>
          <div>
            <AuthoringIsbnModal
              modalId={AUTHORING_ISBN_MODAL_ID}
              modalService={modalService}
              handleSave={(selectedEeoIsbns) => {
                mergeState({ selectedEeoIsbns });
                modalService.closeModal(AUTHORING_ISBN_MODAL_ID);
              }}
              selectedEeoIsbns={state.selectedEeoIsbns}
              books={state.books}
            />
          </div>
        </FocusTrap>
      )
    });
  };

  const handleSearchCheckBox = (row) => {
    return (
      <ELSCheckBox
        changeHandler={(e, value) => handleSearchCheck(row, value)}
        checked={isSearchChecked(row, state.selectedSearchResults)}
      />
    );
  };

  const viewFullContentButton = (row) => {
    return (
      <>
        <div>{row.content.length > 400 ? `${row.content.substring(0, 400)}...` : row.content} </div>
        {row.content.length > 400 && (
          <div>
            <button
              type="button"
              className="u-els-anchorize"
              onClick={() => viewBookChunk(row)}
            >
              view full content
            </button>
          </div>
        )}
      </>
    );
  };

  if (!isAdminUser) {
    return null;
  }

  return (

    <>
  {appState?.pendingRequestCount > 0 && <ELSPageLoader />}
      <div className="u-els-padding c-els-authoring">
        <div className="o-els-container">
          <h2>Authoring POC</h2>
        </div>

        {
          state.response && (
            <div className="o-els-container ">
              <div className="c-els-card ">
                <div className="c-els-card__content">
                  <h3 className="u-els-margin-bottom">Generated Response</h3>
                  <div>
                    <button
                      type="button"
                      className="u-els-anchorize"
                      onClick={handleCopy}
                    >
                      copy
                    </button>
                  </div>
                  <div className="o-els-container">
                    <pre className="c-els-authoring__pre">{state.response}</pre>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        <div className="o-els-container">

          <div className="c-els-card ">
            <div className="c-els-card__content">
              <div>
                <h3 className="u-els-margin-bottom">Prompt</h3>
                <div className="c-els-field">
                  <textarea
                    name="converter"
                    className="c-els-field__input"
                    value={state.prompt}
                    onChange={(e) => {
                    const { value } = e.target;
                    setState((prevState) => {
                      return {
                        ...prevState,
                        prompt: value
                      };
                    });
                  }}
                />
                </div>
              </div>
              <div className="o-els-container">
                <FlexLayout modifiers={[
                  FlexLayoutModifier.LEFT,
                  FlexLayoutModifier.GUTTERS_2X,
                ]}>
                  <FlexItem>
                    <button
                      type="button"
                      className="c-els-button c-els-button--secondary c-els-button--small"
                      onClick={viewPromptPreview}
                    >
                      View prompt preview
                    </button>
                  </FlexItem>
                  <FlexItem>
                    <button
                      type="button"
                      className="c-els-button c-els-button--small"
                      onClick={handlePrompt}
                    >
                      Send final prompt to AI
                    </button>
                  </FlexItem>

                </FlexLayout>

              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="c-els-card ">
            <div className="c-els-card__content">
              <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_2X]}>
                <FlexItem classes={['u-els-width-1o1']}>
                  <h3>Book chunks</h3>

                  <div className="o-els-container">
                    <ELSTextBox
                      name="editChatTitleInput"
                      value={state.search}
                      changeHandler={(e, title) => {
                        mergeState({
                          search: title
                        });
                      }}>
                      Search
                    </ELSTextBox>

                    <FlexLayout modifiers={[
                      FlexLayoutModifier.GUTTERS_1o2,
                      FlexLayoutModifier.LEFT
                    ]}>
                      <FlexItem>
                        <button
                          type="button"
                          className="u-els-anchorize"
                          onClick={handleSelectedIsbnModalOpen}
                        >
                          Searching {Object.keys(state.selectedEeoIsbns).filter((key) => {
                          return state.selectedEeoIsbns[key];
                        }).length} books (click to update)
                        </button>
                      </FlexItem>
                    </FlexLayout>
                  </div>
                  <div className="o-els-container">
                    <button
                      type="button"
                      className="c-els-button c-els-button--small"
                      onClick={handleSearch}
                    >
                      Search
                    </button>
                  </div>
                  {state.searchResults.length > 0 && (
                    <div className="o-els-container">
                      <div className="o-els-container">
                        <FlexLayout modifiers={[
                          FlexLayoutModifier.LEFT,
                          FlexLayoutModifier.GUTTERS
                        ]}>
                          <FlexItem>
                            <button
                              type="button"
                              className="u-els-anchorize"
                              onClick={selectAll}
                            >
                              select all
                            </button>
                          </FlexItem>
                          <FlexItem>
                            <button
                              type="button"
                              className="u-els-anchorize"
                              onClick={deselectAll}
                            >
                              deselect all
                            </button>
                          </FlexItem>
                        </FlexLayout>

                      </div>

                      <h4>Search results</h4>
                      <div className="o-els-container">
                        <ELSDataTable
                          data={getTableData()}
                          noWrap
                          sortIconSize="1o2"
                          columns={[
                            {
                              header:" ",
                              customRender:handleSearchCheckBox
                            },
                            {
                              header:"book",
                              field:"book",
                              sortable:true
                            },
                            {
                              header : "isbn",
                              field : "isbn",
                              sortable: true
                            },
                            {
                              header:"chapter",
                              field:"chapter",
                              sortable:true
                            },
                            {
                              header: 'chapter number',
                              field: 'chapterNumber',
                              sortable:true
                            },
                            {
                              header:"section",
                              field:"section",
                              sortable:true
                            },
                            {
                              header:"page",
                              field:"page",
                              sortable:true
                            },
                            {
                              header:"search relevance",
                              field:"score",
                              sortable:true
                            },
                            {
                              field:"content",
                              header:"content",
                              customRender:viewFullContentButton
                            }
                          ]}
                        />
                      </div>
                    </div>
                  )}
                </FlexItem>
              </FlexLayout>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// withPageLoader must come after connect
const enhance = flowRight(
  ELSWithToastService,
  ELSWithModalService,
  withHTMLHeadSEO({ title: 'Authoring POC' }),
  withPageLoader,
);

const Authoring = enhance(AuthoringComponent) as React.FC<RecordEmpty>;

export default Authoring;
